import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  LayoutGrid, 
  List, 
  Search, 
  Filter, 
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Plus,
  FolderOpen,
  CheckCircle,
  Clock,
  TrendingUp,
  Zap,
  X,
  XCircle
} from 'lucide-react';
import axios from 'axios';

// Import sub-components
import StatCard from './StatCard';
import WelcomeBanner from './WelcomeBanner';
import FavoriteCampaignsStrip from './FavoriteCampaignsStrip';
import CampaignCardV2 from './CampaignCardV2';

// Import theme context
import { useColorTheme } from '../../context/ColorThemeContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * DashboardV2 - Modern redesigned dashboard with glassmorphism
 * Bento grid layout, stat cards, and campaign management
 * Supports theme colors: green, purple, blue
 */
const DashboardV2 = ({ 
  onViewCampaign, 
  onCreateNewCampaign, 
  onEditCampaign,
  onRegenerateCampaign,
  onNavigateToContentStudio,
  sessionToken,
  user,
  refreshTrigger = 0
}) => {
  // Get theme from context
  const { colorTheme, currentColors } = useColorTheme();
  
  // Campaign state
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const campaignsPerPage = 12;
  
  // Search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    content_type: '',
    status: ''
  });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // View mode
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  
  // Selection state
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  
  // Refs
  const fetchControllerRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Stats computed from campaigns
  const stats = {
    total: totalCampaigns,
    completed: campaigns.filter(c => c.generation_status === 'completed' || !c.generation_status).length,
    generating: campaigns.filter(c => c.generation_status === 'generating' || c.generation_status === 'queued').length,
    successRate: campaigns.length > 0 
      ? Math.round((campaigns.filter(c => c.generation_status === 'completed' || !c.generation_status).length / campaigns.length) * 100)
      : 0
  };

  // Fetch campaigns
  const fetchCampaigns = useCallback(async (page = 1) => {
    if (!sessionToken) return;
    
    // Cancel previous request
    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort();
    }
    fetchControllerRef.current = new AbortController();
    
    setIsLoading(true);
    
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', campaignsPerPage.toString());
      
      if (searchTerm) params.append('search', searchTerm);
      if (filters.category) params.append('category', filters.category);
      if (filters.content_type) params.append('content_type', filters.content_type);
      if (filters.status) params.append('status', filters.status);
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      
      const response = await axios.get(`${API}/api/campaigns?${params}`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
        signal: fetchControllerRef.current.signal
      });
      
      let campaignsData = response.data;
      let totalCount = 0;
      
      if (response.data?.campaigns && Array.isArray(response.data.campaigns)) {
        campaignsData = response.data.campaigns;
        totalCount = response.data.total_count || campaignsData.length;
      } else if (Array.isArray(response.data)) {
        campaignsData = response.data;
        totalCount = campaignsData.length;
      }
      
      setCampaigns(campaignsData);
      setTotalCampaigns(totalCount);
      setCurrentPage(page);
      setError(null);
    } catch (err) {
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        setError('Failed to load campaigns');
        console.error('Error fetching campaigns:', err);
      }
    } finally {
      setIsLoading(false);
    }
  }, [sessionToken, searchTerm, filters, sortBy, sortOrder]);

  // Initial fetch and refresh
  useEffect(() => {
    fetchCampaigns(currentPage);
  }, [fetchCampaigns, currentPage, refreshTrigger]);

  // Auto-refresh for generating campaigns
  useEffect(() => {
    const hasGenerating = campaigns.some(c => 
      c.generation_status === 'generating' || c.generation_status === 'queued'
    );
    
    if (hasGenerating) {
      const interval = setInterval(() => fetchCampaigns(currentPage), 10000);
      return () => clearInterval(interval);
    }
  }, [campaigns, currentPage, fetchCampaigns]);

  // Debounced search
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchCampaigns(1);
    }, 300);
  };

  // Selection handlers
  const handleSelectCampaign = (campaignId) => {
    setSelectedCampaigns(prev => 
      prev.includes(campaignId) 
        ? prev.filter(id => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCampaigns.length === campaigns.length) {
      setSelectedCampaigns([]);
    } else {
      setSelectedCampaigns(campaigns.map(c => c.id));
    }
  };

  // Pagination
  const totalPages = Math.ceil(totalCampaigns / campaignsPerPage);

  // Clear filters
  const clearFilters = () => {
    setFilters({ category: '', content_type: '', status: '' });
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Delete handler (placeholder - should be connected to actual delete logic)
  const handleDelete = async (campaign) => {
    if (!window.confirm(`Are you sure you want to delete "${campaign.product_name}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/api/campaigns/${campaign.id}`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      fetchCampaigns(currentPage);
    } catch (err) {
      console.error('Error deleting campaign:', err);
      alert('Failed to delete campaign');
    }
  };

  // Share handler (placeholder)
  const handleShare = (campaign) => {
    // This should open the share modal - connect to parent component
    console.log('Share campaign:', campaign.id);
  };

  // Toggle favorite on a campaign — optimistic update, rolls back on failure.
  const handleToggleCampaignFavorite = async (campaign, wantFav) => {
    const prevCampaigns = campaigns;
    setCampaigns(prev => prev.map(c => c.id === campaign.id ? { ...c, is_favorite: wantFav } : c));
    try {
      await axios.post(
        `${API}/api/campaigns/${campaign.id}/favorite`,
        { is_favorite: wantFav },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
    } catch (err) {
      console.error('Toggle favorite failed:', err);
      setCampaigns(prevCampaigns);
    }
  };

  // Retry handler — resumes a failed campaign via /campaigns/{id}/retry
  const handleRetry = async (campaign) => {
    try {
      await axios.post(
        `${API}/api/campaigns/${campaign.id}/retry`,
        {},
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      // Refresh immediately to show "queued/generating" state
      fetchCampaigns(currentPage);
    } catch (err) {
      console.error('Error retrying campaign:', err);
      alert(err.response?.data?.detail || 'Failed to retry campaign generation. Please try again.');
    }
  };

  // Kill handler — marks a stuck queued/generating campaign as failed so the
  // user can retry cleanly. In-flight Kie AI work is decoupled — recovered
  // URLs still land in kie_tasks and can be attached later if needed.
  const handleKill = async (campaign) => {
    if (!window.confirm(`Kill campaign "${campaign?.product?.product_name || campaign.id}"? This marks it as failed so you can retry.`)) return;
    try {
      await axios.post(
        `${API}/api/campaigns/${campaign.id}/kill`,
        {},
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      fetchCampaigns(currentPage);
    } catch (err) {
      console.error('Error killing campaign:', err);
      alert(err.response?.data?.detail || 'Failed to kill campaign. Please try again.');
    }
  };

  // Global bulk kill — kills every queued/generating campaign for the user
  // that has been stuck > 5 minutes. Confirms with the user first.
  const handleKillAllStuck = async () => {
    if (!window.confirm('Kill ALL stuck campaigns (queued/generating > 5 min)? This marks them as failed. You can retry them individually.')) return;
    try {
      const resp = await axios.post(
        `${API}/api/campaigns/kill-all-stuck?older_than_minutes=5`,
        {},
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      alert(resp?.data?.message || `Killed ${resp?.data?.killed_count || 0} campaigns.`);
      fetchCampaigns(currentPage);
    } catch (err) {
      console.error('Kill-all-stuck failed:', err);
      alert(err.response?.data?.detail || 'Failed to kill stuck campaigns.');
    }
  };

  // Theme-aware background gradients
  const themeBgGradients = {
    green: 'from-emerald-50 via-white to-teal-50 dark:from-slate-900 dark:via-slate-900 dark:to-emerald-950/30',
    purple: 'from-purple-50 via-white to-pink-50 dark:from-slate-900 dark:via-slate-900 dark:to-purple-950/30',
    blue: 'from-blue-50 via-white to-sky-50 dark:from-slate-900 dark:via-slate-900 dark:to-blue-950/30'
  };

  // Theme-aware accent colors for buttons and interactive elements
  const themeAccents = {
    green: {
      primary: 'bg-emerald-500 hover:bg-emerald-600',
      light: 'bg-emerald-100 dark:bg-emerald-500/20',
      text: 'text-emerald-600 dark:text-emerald-400',
      ring: 'focus:ring-emerald-500/50',
      border: 'focus:border-emerald-500'
    },
    purple: {
      primary: 'bg-purple-500 hover:bg-purple-600',
      light: 'bg-purple-100 dark:bg-purple-500/20',
      text: 'text-purple-600 dark:text-purple-400',
      ring: 'focus:ring-purple-500/50',
      border: 'focus:border-purple-500'
    },
    blue: {
      primary: 'bg-blue-500 hover:bg-blue-600',
      light: 'bg-blue-100 dark:bg-blue-500/20',
      text: 'text-blue-600 dark:text-blue-400',
      ring: 'focus:ring-blue-500/50',
      border: 'focus:border-blue-500'
    }
  };

  const bgGradient = themeBgGradients[colorTheme] || themeBgGradients.green;
  const accent = themeAccents[colorTheme] || themeAccents.green;

  return (
    <div 
      className={`min-h-screen bg-gradient-to-br ${bgGradient}`}
      data-testid="dashboard-v2"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Welcome Banner */}
        <WelcomeBanner 
          userName={user?.name || user?.email?.split('@')[0] || 'Creator'}
          onCreateNew={onCreateNewCampaign}
          onQuickAction={onNavigateToContentStudio}
          colorTheme={colorTheme}
        />

        {/* Stats Grid - Bento Layout */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard 
            title="Total Campaigns"
            value={stats.total}
            icon={FolderOpen}
            colorTheme={colorTheme}
          />
          <StatCard 
            title="Completed"
            value={stats.completed}
            icon={CheckCircle}
            colorTheme={colorTheme}
            trend="up"
            trendValue="+12%"
          />
          <StatCard 
            title="In Progress"
            value={stats.generating}
            icon={Clock}
            colorTheme={colorTheme}
          />
          <StatCard 
            title="Success Rate"
            value={`${stats.successRate}%`}
            icon={TrendingUp}
            colorTheme={colorTheme}
          />
        </div>

        {/* Favorite campaigns strip (auto-hides when the user has no ⭐ picks yet) */}
        <FavoriteCampaignsStrip
          sessionToken={sessionToken}
          colorTheme={colorTheme}
          onOpenCampaign={(id) => {
            if (onViewCampaign) onViewCampaign({ id });
          }}
        />

        {/* Main Content Card */}
        <div className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl rounded-3xl border border-slate-200/50 dark:border-slate-700/50 shadow-xl overflow-hidden">
          
          {/* Toolbar */}
          <div className="px-6 py-4 border-b border-slate-200/50 dark:border-slate-700/50">
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
              
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search campaigns..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className={`
                    w-full pl-10 pr-4 py-2.5
                    bg-slate-100/50 dark:bg-slate-700/50
                    border border-slate-200 dark:border-slate-600
                    rounded-xl
                    text-slate-900 dark:text-white
                    placeholder-slate-400
                    focus:outline-none focus:ring-2 ${accent.ring} ${accent.border}
                    transition-all duration-200
                  `}
                  data-testid="campaign-search"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3">
                {/* Results count */}
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  {totalCampaigns} campaign{totalCampaigns !== 1 ? 's' : ''}
                </span>

                {/* Filter toggle */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`
                    inline-flex items-center gap-2 px-4 py-2.5
                    rounded-xl text-sm font-medium
                    transition-all duration-200
                    ${showFilters 
                      ? `${accent.primary} text-white` 
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }
                  `}
                  data-testid="filter-toggle"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  Filters
                </button>

                {/* View mode toggle */}
                <div className="flex items-center bg-slate-100 dark:bg-slate-700 rounded-xl p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg transition-colors ${
                      viewMode === 'grid' 
                        ? 'bg-white dark:bg-slate-600 shadow-sm' 
                        : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                    }`}
                    data-testid="view-grid"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg transition-colors ${
                      viewMode === 'list' 
                        ? 'bg-white dark:bg-slate-600 shadow-sm' 
                        : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                    }`}
                    data-testid="view-list"
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>

                {/* Refresh */}
                <button
                  onClick={() => fetchCampaigns(currentPage)}
                  className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                  data-testid="refresh-campaigns"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                </button>

                {/* Kill All Stuck — bulk force-fail any queued/generating campaign
                    older than 5 min so the user can retry cleanly. */}
                <button
                  onClick={handleKillAllStuck}
                  className="p-2.5 rounded-xl bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                  title="Kill all stuck campaigns (queued/generating > 5 min)"
                  data-testid="kill-all-stuck-btn"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-slate-200/50 dark:border-slate-700/50">
                <div className="flex flex-wrap gap-4 items-center">
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-700 border-0 text-sm text-slate-700 dark:text-slate-300 focus:ring-2 focus:ring-indigo-500"
                    data-testid="filter-status"
                  >
                    <option value="">All Status</option>
                    <option value="completed">Completed</option>
                    <option value="generating">Generating</option>
                    <option value="queued">Queued</option>
                    <option value="failed">Failed</option>
                  </select>

                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-700 border-0 text-sm text-slate-700 dark:text-slate-300 focus:ring-2 focus:ring-indigo-500"
                    data-testid="sort-by"
                  >
                    <option value="created_at">Date Created</option>
                    <option value="product_name">Name</option>
                    <option value="category">Category</option>
                  </select>

                  <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-700 border-0 text-sm text-slate-700 dark:text-slate-300 focus:ring-2 focus:ring-indigo-500"
                    data-testid="sort-order"
                  >
                    <option value="desc">Newest First</option>
                    <option value="asc">Oldest First</option>
                  </select>

                  <button
                    onClick={clearFilters}
                    className="inline-flex items-center gap-1 px-3 py-2 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                    data-testid="clear-filters"
                  >
                    <X className="w-4 h-4" />
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Campaign Grid/List */}
          <div className="p-6">
            {isLoading && campaigns.length === 0 ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <RefreshCw className="w-10 h-10 text-indigo-500 animate-spin mx-auto mb-4" />
                  <p className="text-slate-500 dark:text-slate-400">Loading campaigns...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <p className="text-rose-500 mb-4">{error}</p>
                  <button
                    onClick={() => fetchCampaigns(currentPage)}
                    className="px-4 py-2 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : campaigns.length === 0 ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center max-w-md">
                  <div className={`w-20 h-20 ${accent.light} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                    <Zap className={`w-10 h-10 ${accent.text}`} />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                    No campaigns yet
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400 mb-6">
                    Create your first AI-powered marketing campaign and watch the magic happen.
                  </p>
                  <button
                    onClick={onCreateNewCampaign}
                    className={`inline-flex items-center gap-2 px-6 py-3 ${accent.primary} text-white font-medium rounded-xl transition-colors`}
                    data-testid="create-first-campaign"
                  >
                    <Plus className="w-5 h-5" />
                    Create Campaign
                  </button>
                </div>
              </div>
            ) : (
              <div className={`
                ${viewMode === 'grid' 
                  ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' 
                  : 'flex flex-col gap-4'
                }
              `}>
                {campaigns.map(campaign => (
                  <CampaignCardV2
                    key={campaign.id}
                    campaign={campaign}
                    onView={onViewCampaign}
                    onEdit={onEditCampaign}
                    onDuplicate={onRegenerateCampaign}
                    onDelete={handleDelete}
                    onShare={handleShare}
                    onRetry={handleRetry}
                    onKill={handleKill}
                    onToggleFavorite={handleToggleCampaignFavorite}
                    isSelected={selectedCampaigns.includes(campaign.id)}
                    onSelect={handleSelectCampaign}
                    colorTheme={colorTheme}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-slate-200/50 dark:border-slate-700/50">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Page {currentPage} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid="prev-page"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  
                  {/* Page numbers */}
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`
                            w-10 h-10 rounded-xl text-sm font-medium transition-colors
                            ${currentPage === pageNum 
                              ? `${accent.primary} text-white` 
                              : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                            }
                          `}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid="next-page"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardV2;
